import {
    INTEGER_STRING_MAX,
    isConformantIntegerString,
} from '~/domain/dicom/valueRepresentation';
/**
 * `SeriesNumber (0020,0011)` for a derived series.
 *
 * The attribute is Type 2 and the standard requires no uniqueness —
 * `SeriesInstanceUID` is the identifier. But it is what worklists sort and
 * display by, and a shared number within a study invites confusion, so it is
 * treated here as though it had to be unique.
 *
 * The rule is the next free number rather than an offset from the source.
 * An offset (the previous `source + 1000`) reads nicely but collides the
 * second time the same series is reformatted, which is the ordinary case:
 * reformat, look at it, reformat again with a different thickness. Where a
 * derived series came from is already carried properly by `ImageType`
 * DERIVED and `DerivationDescription`, so the number is free to serve the
 * ordering it is actually used for.
 */

/** Lowest number a series may carry. */
const FIRST_SERIES_NUMBER = 1;

/**
 * Reaching this means the study is already unusable, but a number the
 * writer cannot encode must not be produced silently.
 */
const MAX_SERIES_NUMBER = INTEGER_STRING_MAX;

/**
 * Whether a `SeriesNumber` can be written as an Integer String.
 *
 * `nextFreeSeriesNumber` guarantees this of the number it proposes, but
 * the user may type another, and the writer has no range of its own.
 */
export function isWritableSeriesNumber(seriesNumber: number): boolean {
    return (
        isConformantIntegerString(seriesNumber) &&
        seriesNumber >= FIRST_SERIES_NUMBER
    );
}

/**
 * One past the highest number in use, so the derived series sorts after
 * everything it was derived from rather than in among it.
 *
 * Throws rather than wrapping or reusing a number when the range is
 * exhausted: a duplicate is the failure this function exists to avoid.
 */
export function nextFreeSeriesNumber(used: ReadonlySet<number>): number {
    let highest = FIRST_SERIES_NUMBER - 1;
    for (const value of used) {
        if (Number.isFinite(value) && value > highest) {
            highest = value;
        }
    }
    const next = Math.max(FIRST_SERIES_NUMBER, Math.floor(highest) + 1);
    if (next > MAX_SERIES_NUMBER) {
        throw new Error(
            'No free SeriesNumber remains below the Integer String limit'
        );
    }
    return next;
}

/**
 * The numbers among `candidates` that a series could actually carry.
 *
 * `SeriesNumber` is an Integer String, so it arrives as a number, a
 * string, or absent depending on the parser and the source. Anything that
 * is not a finite number is dropped rather than coerced to 0 — a missing
 * number is not series 0, and treating it as one would make
 * {@link nextFreeSeriesNumber} avoid a number nothing uses.
 */
export function collectSeriesNumbers(
    candidates: Iterable<unknown>
): Set<number> {
    const used = new Set<number>();
    for (const candidate of candidates) {
        const value = Number(candidate);
        if (candidate !== null && candidate !== '' && Number.isFinite(value)) {
            used.add(value);
        }
    }
    return used;
}
